/**
 * JWT session lifecycle — textbook REFRESH TOKEN flow.
 *
 * The access JWT is short (`JWT_EXPIRY_MINUTES`, default 15'); the
 * long session is the opaque rotated refresh token stored server-side
 * (`hub.auth.sessions`). Before each API call:
 *
 *   - access token expired or near expiry (< REFRESH_WINDOW_MS) AND a
 *     refresh token is present → POST /auth/refresh, store the NEW
 *     pair (every refresh ROTATES: the old refresh value is retired).
 *   - refresh rejected (revoked / expired / reuse-detected / kick-out)
 *     → logout immediately; ProtectedRoute bounces to login carrying
 *     `location.state.from`, so re-auth lands back on the same page.
 *
 * Because the server can revoke the refresh chain (logout, admin
 * "disconnect", password reset) and the access JWT carries the user's
 * `token_version`, a kick-out bites at the very next request — there
 * is no client-side way around it.
 */
import { useAuthStore } from "./auth";
import { queryClient } from "./query";

/** Refresh when less than this remains of the ACCESS token's life.
 *  With 15' tokens this means roughly one refresh every ~10 minutes
 *  of activity. */
export const REFRESH_WINDOW_MS = 5 * 60 * 1000;

/** Decode the `exp` claim (ms epoch) without verifying the signature —
 *  the client only needs the timestamp; the server stays the judge of
 *  validity. Returns null for non-JWT bearers (API keys) or garbage. */
export function tokenExpiryMs(token: string): number | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
    ) as { exp?: unknown };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Pure decision: refresh when expired OR inside the window. (Unlike a
 *  sliding-JWT scheme, an EXPIRED access token is recoverable here —
 *  the refresh token is the credential, not the JWT.) */
export function shouldRefresh(expiryMs: number, nowMs: number): boolean {
  return expiryMs - nowMs <= REFRESH_WINDOW_MS;
}

// Single-flight: concurrent API calls share one refresh request.
let inFlight: Promise<void> | null = null;

/**
 * Called by the api client before each request. No-op without a
 * refresh token or while the access token has plenty of life. A
 * rejected refresh logs the session out (the server said no — revoked,
 * expired, or stolen-token family kill); a NETWORK failure is
 * swallowed and the original request proceeds with the old token.
 */
export async function maybeRefreshSession(): Promise<void> {
  const { token, refreshToken, user } = useAuthStore.getState();
  if (!token || !user || !refreshToken || token.startsWith("tray_")) return;
  const exp = tokenExpiryMs(token);
  if (exp === null || !shouldRefresh(exp, Date.now())) return;

  inFlight ??= fetch(new URL("/api/admin/auth/refresh", window.location.origin), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
    .then(async (r) => {
      const state = useAuthStore.getState();
      // A logout may have raced the refresh — don't resurrect it.
      if (state.refreshToken !== refreshToken || !state.user) return;
      if (r.ok) {
        const data = (await r.json()) as {
          access_token?: string;
          refresh_token?: string;
        };
        if (data.access_token && data.refresh_token) {
          state.setSession(data.access_token, state.user, data.refresh_token);
        }
      } else if (r.status === 401 || r.status === 403) {
        // The server refused the refresh: session is gone (revoked /
        // expired / kick-out). End it client-side too.
        state.logout();
      }
    })
    .catch(() => undefined)
    .finally(() => {
      inFlight = null;
    });
  await inFlight;
}

/** Set for ONE login render right after a voluntary logout (see `endSession`). */
export const VOLUNTARY_LOGOUT_KEY = "tray:auth:just-logged-out";

/** True once, then cleared: the caller is rendering the login right after a logout. */
export function consumeJustLoggedOut(): boolean {
  try {
    const hit = window.sessionStorage.getItem(VOLUNTARY_LOGOUT_KEY) === "1";
    if (hit) window.sessionStorage.removeItem(VOLUNTARY_LOGOUT_KEY);
    return hit;
  } catch {
    return false;
  }
}

/** Voluntary logout: retire the refresh family server-side (so a copy
 *  of the value can't resume the session), then clear the store.
 *  Best-effort — the local logout always happens. */
export async function endSession(): Promise<void> {
  const { refreshToken } = useAuthStore.getState();
  if (refreshToken) {
    try {
      await fetch(new URL("/api/admin/auth/logout", window.location.origin), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch {
      // Offline logout is still a logout.
    }
  }
  useAuthStore.getState().logout();
  // Remember that this was a VOLUNTARY logout, for exactly one login render. Signing out of
  // a workspace does NOT sign you out of the Account Tray — so "Accedi con Account Tray"
  // walks straight back in without asking for anything, which reads as a broken logout. The
  // login page uses this flag to say so; sessionStorage because it must not outlive the tab.
  try {
    window.sessionStorage.setItem(VOLUNTARY_LOGOUT_KEY, "1");
  } catch {
    // Private mode / storage disabled: the note is a courtesy, never a requirement.
  }
  // Drop every cached query (notably ["me"] permissions + any
  // admin-scoped data) so nothing stale survives into the next session.
  // The login that follows does a hard reload anyway, but this keeps the
  // transient login-page render clean and covers programmatic re-auth.
  queryClient.clear();
}
